import {
  ChevronDownIcon,
  ChevronUpIcon,
  PlusIconBorderless,
} from "$components/icons";
import { useAnalytics } from "$global-providers/analytics-provider/provider";
import { InvestigationRecommendationLocation } from "$routes/patient/[:patientId]/consultation/new/page";
import { Typography } from "@material-ui/core";
import React, { useState } from "react";
import { FormattedMessage } from "react-intl";
import type { DiseaseRecommendation } from "./clinical-decision-support-service/mappers";
import { RecommendationsBox } from "./recommendations-box";

const RecommendedActionsCardBase: React.FC<{
  diseasesRecommendations: DiseaseRecommendation[];
  onAddInvestigation: (catalogueItemLoincCode: string) => void;
  trackingId: string;
  patientId: string;
}> = ({
  diseasesRecommendations,
  onAddInvestigation,
  trackingId,
  patientId,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [openDiseaseId, setOpenDiseaseId] = useState<string | null>(null);

  const { trackEvent } = useAnalytics();
  const handleClick = (
    event: React.MouseEvent<HTMLElement>,
    actionId: string,
  ) => {
    setAnchorEl(event.currentTarget);
    setOpenDiseaseId(openDiseaseId === actionId ? null : actionId);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setOpenDiseaseId(null);
  };

  if (diseasesRecommendations.length === 0) return null;

  const recommendations = diseasesRecommendations.map((recommendation) => ({
    id: recommendation.id,
    label: (
      <FormattedMessage
        id="patient.new.consultation.form.recommendedActions.investigation"
        defaultMessage="Add {investigationDisplayName} investigation"
        values={{ investigationDisplayName: recommendation.diseaseDisplayName }}
      />
    ),
    endIcon:
      openDiseaseId === recommendation.id ? (
        <ChevronUpIcon />
      ) : (
        <ChevronDownIcon />
      ),
    menuItems: recommendation.investigations.map((investigation, index) => ({
      key: `recommended-investigation-${investigation.catalogueItemLoincCode}`,
      label: investigation.displayName,
      onClick: () => {
        trackEvent("Investigation RecommendationSelected", {
          recommendationId: recommendation.id,
          recommendationInvestigation: investigation.displayName,
          recommendationCatalogueItemLoincCode:
            investigation.catalogueItemLoincCode,
          recommendationCode: investigation.code,
          recommendationLocation:
            InvestigationRecommendationLocation.RECOMMENDED_ACTIONS_CARD,
          patientId,
          index,
          trackingId,
        });

        onAddInvestigation(investigation.catalogueItemLoincCode);
        handleClose();
      },
      adornment: <PlusIconBorderless />,
    })),
  }));

  return (
    <RecommendationsBox
      TitleText="Recommended actions"
      SubtitleText={
        <Typography variant="body1" color="textSecondary">
          <FormattedMessage
            id="patient.new.consultation.form.recommendedActions.subtitle"
            defaultMessage="Based on this patient's consultation data"
          />
        </Typography>
      }
      recommendations={recommendations}
      handleClick={handleClick}
      menuAnchorEl={anchorEl}
      menuTrackingId={openDiseaseId}
      menuHandleClose={handleClose}
    />
  );
};

export const RecommendedActionsCard = React.memo(RecommendedActionsCardBase);
